import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import styles from "./MatchDetail.module.css";
import MatchDetailAPI from "../../data/matchDetails";
import {
  FaArrowLeft,
  FaTrophy,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaCoins,
  FaUserTie,
  FaClipboardList,
  FaPlay,
  FaSyncAlt
} from "react-icons/fa";
import { useMatchDetailState } from "../../states/useMatchDetailState";
const MatchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { match, setMatch, team1Players, setTeam1Players, team2Players, setTeam2Players, loading, setLoading, playersLoading, setPlayersLoading, error, setError, apiLoading, setApiLoading, scorecardData, setScorecardData } = useMatchDetailState()
  const handleMatchDetailData = (data) => {

    let matchDetail;

    if (Array.isArray(data) && data.length > 0) {
      matchDetail = data[0];
    } else if (data && data.match_details && Array.isArray(data.match_details) && data.match_details.length > 0) {
      matchDetail = data.match_details[0];
    } else if (data && data.id) {
      matchDetail = data;
    } else {
      setError("Match details not found or invalid format");
      setApiLoading(false);
      setLoading(false);
      return;
    }

    // Extract basic match info
    const transformedMatch = {
      id: matchDetail.id || matchDetail.match_id,
      match_date: matchDetail.match_date,
      match_time: matchDetail.match_time,
      status: getMatchStatus(matchDetail.status),
      venue: matchDetail.ground_name,
      league_name: matchDetail.league_name,
      competition_name: matchDetail.competition_name,
      match_type: matchDetail.match_type,
      home_team_id: matchDetail.home_team_id,
      away_team_id: matchDetail.away_team_id,
      team1_data: {
        id: matchDetail.home_team_id,
        name: matchDetail.home_team_name,
        club_name: matchDetail.home_club_name,
        short_name: getTeamShortName(matchDetail.home_team_name),
        logo: "/default-team.png",
      },
      team2_data: {
        id: matchDetail.away_team_id,
        name: matchDetail.away_team_name,
        club_name: matchDetail.away_club_name,
        short_name: getTeamShortName(matchDetail.away_team_name),
        logo: "/default-team.png",
      },
      // Get scores from innings
      score_team1: getTeamScore(matchDetail.innings, matchDetail.home_team_id),
      score_team2: getTeamScore(matchDetail.innings, matchDetail.away_team_id),
      overs: getOversInfo(matchDetail.innings),
      toss: matchDetail.toss,
      result_description: matchDetail.result_description,
      match_notes: matchDetail.match_notes,
      umpires: getUmpiresInfo(matchDetail),
      scorers: getScorersInfo(matchDetail),
      points: matchDetail.points || [],
      innings: matchDetail.innings || [],
      players: matchDetail.players || [],
      result: matchDetail.result,
      result_applied_to: matchDetail.result_applied_to,
    };

    setMatch(transformedMatch);
    setApiLoading(false);

    // Extract players data
    extractPlayersData(matchDetail);

    // Extract scorecard data
    extractScorecardData(matchDetail);
  };

  // Extract detailed scorecard data
  const extractScorecardData = (matchDetail) => {
    if (!matchDetail.innings || matchDetail.innings.length === 0) {
      setScorecardData(null);
      return;
    }

    const scorecard = {
      innings1: null,
      innings2: null
    };

    // First innings
    if (matchDetail.innings[0]) {
      const innings1 = matchDetail.innings[0];
      scorecard.innings1 = {
        team_name: innings1.team_batting_name,
        team_id: innings1.team_batting_id,
        runs: innings1.runs,
        wickets: innings1.wickets,
        overs: innings1.overs,
        total_extras: innings1.total_extras,
        extra_byes: innings1.extra_byes || "0",
        extra_leg_byes: innings1.extra_leg_byes || "0",
        extra_wides: innings1.extra_wides || "0",
        extra_no_balls: innings1.extra_no_balls || "0",
        batting: innings1.bat || [],
        bowling: innings1.bowl || [],
        fow: innings1.fow || []
      };
    }

    // Second innings
    if (matchDetail.innings[1]) {
      const innings2 = matchDetail.innings[1];
      scorecard.innings2 = {
        team_name: innings2.team_batting_name,
        team_id: innings2.team_batting_id,
        runs: innings2.runs,
        wickets: innings2.wickets,
        overs: innings2.overs,
        total_extras: innings2.total_extras,
        extra_byes: innings2.extra_byes || "0",
        extra_leg_byes: innings2.extra_leg_byes || "0",
        extra_wides: innings2.extra_wides || "0",
        extra_no_balls: innings2.extra_no_balls || "0",
        batting: innings2.bat || [],
        bowling: innings2.bowl || [],
        fow: innings2.fow || []
      };
    }

    setScorecardData(scorecard);
  };

  // Handle API error
  const handleAPIError = (errorMsg) => {
    console.error("Match Detail API Error:", errorMsg);
    setError(`Failed to load match details: ${errorMsg}`);
    setApiLoading(false);
    setLoading(false);
  };

  // Handle API loading
  const handleAPILoading = (isLoading) => {
    setApiLoading(isLoading);
  };

  // Extract players data from match detail - IMPROVED BOWLER DETECTION
  const extractPlayersData = (matchDetail) => {
    try {
      setPlayersLoading(true);

      let homePlayers = [];
      let awayPlayers = [];

      // Extract players from API response
      if (matchDetail.players && matchDetail.innings) {
        // Create maps of batting and bowling stats for each player
        const battingStatsMap = createBattingStatsMap(matchDetail.innings);
        const bowlingStatsMap = createBowlingStatsMap(matchDetail.innings);

        // Process each team's players
        matchDetail.players.forEach((teamPlayers) => {
          // Check for home_team property
          if (teamPlayers.home_team && Array.isArray(teamPlayers.home_team)) {
            homePlayers = teamPlayers.home_team.map((player) => {
              const playerId = player.player_id ? player.player_id.toString() : null;
              const playerName = player.player_name || "Unknown Player";

              // Get role based on batting/bowling performance and position
              const role = determinePlayerRole(
                player,
                playerId,
                playerName, // Pass player name for better bowler matching
                battingStatsMap,
                bowlingStatsMap
              );

              return {
                id: playerId || `${matchDetail.home_team_id}_${player.position}`,
                name: playerName,
                role: role,
                position: player.position,
                is_captain: player.captain || false,
                is_wicket_keeper: player.wicket_keeper || false,
                batting_stats: battingStatsMap[playerId] || battingStatsMap[playerName] || {
                  runs: 0,
                  balls: 0,
                  fours: 0,
                  sixes: 0,
                  how_out: "DNB"
                },
                bowling_stats: bowlingStatsMap[playerId] || bowlingStatsMap[playerName] || {
                  overs: 0,
                  maidens: 0,
                  runs: 0,
                  wickets: 0
                }
              };
            });
          }

          // Check for away_team property
          if (teamPlayers.away_team && Array.isArray(teamPlayers.away_team)) {
            awayPlayers = teamPlayers.away_team.map((player) => {
              const playerId = player.player_id ? player.player_id.toString() : null;
              const playerName = player.player_name || "Unknown Player";

              // Get role based on batting/bowling performance and position
              const role = determinePlayerRole(
                player,
                playerId,
                playerName, // Pass player name for better bowler matching
                battingStatsMap,
                bowlingStatsMap
              );

              return {
                id: playerId || `${matchDetail.away_team_id}_${player.position}`,
                name: playerName,
                role: role,
                position: player.position,
                is_captain: player.captain || false,
                is_wicket_keeper: player.wicket_keeper || false,
                batting_stats: battingStatsMap[playerId] || battingStatsMap[playerName] || {
                  runs: 0,
                  balls: 0,
                  fours: 0,
                  sixes: 0,
                  how_out: "DNB"
                },
                bowling_stats: bowlingStatsMap[playerId] || bowlingStatsMap[playerName] || {
                  overs: 0,
                  maidens: 0,
                  runs: 0,
                  wickets: 0
                }
              };
            });
          }

          // Alternative structure: Check if object has team_id property
          if (teamPlayers.team_id) {
            const isHomeTeam = teamPlayers.team_id.toString() === matchDetail.home_team_id;
            if (teamPlayers.players && Array.isArray(teamPlayers.players)) {
              const playerList = teamPlayers.players.map((player) => {
                const playerId = player.player_id ? player.player_id.toString() : null;
                const playerName = player.player_name || "Unknown Player";

                // Get role based on batting/bowling performance and position
                const role = determinePlayerRole(
                  player,
                  playerId,
                  playerName, // Pass player name for better bowler matching
                  battingStatsMap,
                  bowlingStatsMap
                );

                return {
                  id: playerId || `${teamPlayers.team_id}_${player.position}`,
                  name: playerName,
                  role: role,
                  position: player.position,
                  is_captain: player.captain || false,
                  is_wicket_keeper: player.wicket_keeper || false,
                  batting_stats: battingStatsMap[playerId] || battingStatsMap[playerName] || {
                    runs: 0,
                    balls: 0,
                    fours: 0,
                    sixes: 0,
                    how_out: "DNB"
                  },
                  bowling_stats: bowlingStatsMap[playerId] || bowlingStatsMap[playerName] || {
                    overs: 0,
                    maidens: 0,
                    runs: 0,
                    wickets: 0
                  }
                };
              });


              if (isHomeTeam) {
                homePlayers = playerList;
              } else {
                awayPlayers = playerList;

              }
            }
          }
        });
      }

      setTeam1Players(homePlayers);
      setTeam2Players(awayPlayers);
      setPlayersLoading(false);
      setLoading(false);

    } catch (error) {
      console.error("Error extracting players data:", error);
      setPlayersLoading(false);
      setLoading(false);
    }
  };

  // Create batting stats map from innings data
  const createBattingStatsMap = (innings) => {
    const battingStats = {};

    if (!innings) return battingStats;

    innings.forEach(inning => {
      if (inning.bat && Array.isArray(inning.bat)) {
        inning.bat.forEach(batsman => {
          // Try to use batsman_id, player_id, or name as identifier
          const playerId = batsman.batsman_id || batsman.player_id;
          const playerName = batsman.batsman_name;

          if (playerId) {
            const idStr = playerId.toString();
            battingStats[idStr] = {
              runs: parseInt(batsman.runs) || 0,
              balls: parseInt(batsman.balls) || 0,
              fours: parseInt(batsman.fours) || 0,
              sixes: parseInt(batsman.sixes) || 0,
              how_out: batsman.how_out || "DNB",
              position: parseInt(batsman.position) || 0,
              player_name: playerName || "Unknown"
            };
          }

          // Also store by name for fallback matching
          if (playerName) {
            battingStats[playerName] = {
              runs: parseInt(batsman.runs) || 0,
              balls: parseInt(batsman.balls) || 0,
              fours: parseInt(batsman.fours) || 0,
              sixes: parseInt(batsman.sixes) || 0,
              how_out: batsman.how_out || "DNB",
              position: parseInt(batsman.position) || 0,
              player_name: playerName
            };
          }
        });
      }
    });

    return battingStats;
  };

  // Create bowling stats map from innings data - IMPROVED FOR BOWLER DETECTION
  const createBowlingStatsMap = (innings) => {
    const bowlingStats = {};

    if (!innings) return bowlingStats;

    innings.forEach(inning => {
      if (inning.bowl && Array.isArray(inning.bowl)) {
        inning.bowl.forEach(bowler => {
          // In your data, bowlers only have names, no IDs
          const playerName = bowler.bowler_name;

          if (playerName) {
            // Create a normalized key (lowercase, trimmed)
            const normalizedName = playerName.toLowerCase().trim();

            // Initialize or update bowling stats
            if (!bowlingStats[normalizedName]) {
              bowlingStats[normalizedName] = {
                overs: 0,
                maidens: 0,
                runs: 0,
                wickets: 0,
                wides: parseInt(bowler.wides) || 0,
                no_balls: parseInt(bowler.no_balls) || 0,
                player_name: playerName
              };
            }

            // Accumulate stats (in case bowler appears in multiple innings)
            bowlingStats[normalizedName].overs += parseFloat(bowler.overs) || 0;
            bowlingStats[normalizedName].maidens += parseInt(bowler.maidens) || 0;
            bowlingStats[normalizedName].runs += parseInt(bowler.runs) || 0;
            bowlingStats[normalizedName].wickets += parseInt(bowler.wickets) || 0;

            // Calculate economy rate
            if (bowlingStats[normalizedName].overs > 0) {
              bowlingStats[normalizedName].economy =
                (bowlingStats[normalizedName].runs / bowlingStats[normalizedName].overs).toFixed(2);
            } else {
              bowlingStats[normalizedName].economy = "0.00";
            }

            // Also store by exact name for exact matching
            bowlingStats[playerName] = bowlingStats[normalizedName];
          }
        });
      }
    });

    return bowlingStats;
  };

  // Determine player role based on performance data - IMPROVED BOWLER DETECTION
  const determinePlayerRole = (player, playerId, playerName, battingStatsMap, bowlingStatsMap) => {
    if (player.wicket_keeper) return "wicketkeeper";

    // Normalize player name for matching
    const normalizedPlayerName = playerName ? playerName.toLowerCase().trim() : "";

    // Get stats for this player - try multiple ways to match
    const battingStats =
      (playerId && battingStatsMap[playerId]) ||
      (playerName && battingStatsMap[playerName]) ||
      (normalizedPlayerName && battingStatsMap[normalizedPlayerName]) ||
      null;

    const bowlingStats =
      (playerId && bowlingStatsMap[playerId]) ||
      (playerName && bowlingStatsMap[playerName]) ||
      (normalizedPlayerName && bowlingStatsMap[normalizedPlayerName]) ||
      null;

    // Check if player batted
    const didBat = battingStats && battingStats.how_out !== "DNB";
    const hasBowled = bowlingStats && bowlingStats.overs > 0;

    // SIMPLIFIED ROLE DETERMINATION
    if (didBat && hasBowled) {
      return "allrounder";
    } else if (didBat) {
      return "batsman";
    } else if (hasBowled) {
      return "bowler";
    }

    // Fallback based on position if no performance data
    const position = player.position || 11;
    if (position <= 7) return "batsman";
    return "bowler";
  };

  // Helper functions
  const getMatchStatus = (status) => {
    const statusStr = (status || "").toLowerCase();

    if (statusStr.includes("live") || statusStr.includes("in progress")) return "live";
    if (statusStr.includes("completed") || statusStr.includes("result")) return "completed";
    if (statusStr.includes("abandoned") || statusStr.includes("cancelled")) return "cancelled";

    // For "New" status, check if match date is in past
    if (statusStr.includes("new") || statusStr.includes("upcoming")) {
      return "upcoming";
    }

    return "completed"; // Default to completed for safety
  };

  const getTeamShortName = (teamName) => {
    if (!teamName) return "Undecided";
    // Remove "1st XI", "2nd XI" etc
    const cleanedName = teamName.replace(/1st XI|2nd XI|3rd XI|4th XI/gi, '').trim();
    const words = cleanedName.split(' ');
    if (words.length > 1) {
      // Take first 2-3 letters from each word
      return words.slice(0, 2).map(word => word.substring(0, 3)).join('').toUpperCase();
    }
    return cleanedName.substring(0, 4).toUpperCase();
  };

  const getTeamScore = (innings, teamId) => {
    if (!innings || innings.length === 0) return "0/0";

    const teamIdStr = teamId.toString();

    const teamInnings = innings.find(inn => {
      return inn.team_batting_id === teamIdStr;
    });

    if (teamInnings) {
      const score = `${teamInnings.runs || 0}/${teamInnings.wickets || 0}`;
      return score;
    }

    return "0/0";
  };

  const getOversInfo = (innings) => {
    if (!innings || innings.length === 0) return "0.0";

    let overs = "0.0";
    innings.forEach(inn => {
      if (inn.overs) {
        overs = inn.overs;
      }
    });

    return overs;
  };

  const getUmpiresInfo = (matchDetail) => {
    const umpires = [];
    if (matchDetail.umpire_1_name && matchDetail.umpire_1_name.trim()) umpires.push(matchDetail.umpire_1_name);
    if (matchDetail.umpire_2_name && matchDetail.umpire_2_name.trim()) umpires.push(matchDetail.umpire_2_name);
    if (matchDetail.umpire_3_name && matchDetail.umpire_3_name.trim()) umpires.push(matchDetail.umpire_3_name);
    return umpires;
  };

  const getScorersInfo = (matchDetail) => {
    const scorers = [];
    if (matchDetail.scorer_1_name && matchDetail.scorer_1_name.trim()) scorers.push(matchDetail.scorer_1_name);
    if (matchDetail.scorer_2_name && matchDetail.scorer_2_name.trim()) scorers.push(matchDetail.scorer_2_name);
    return scorers;
  };

  // SIMPLIFIED: Get role display name
  const getRoleDisplayName = (role) => {
    const roleMap = {
      "wicketkeeper": "Wicket-Keeper",
      "batsman": "Batsman",
      "bowler": "Bowler",
      "allrounder": "All-Rounder",
      "all-rounder": "All-Rounder",
      "captain": "Captain",
      "player": "Player"
    };
    return roleMap[role] || role;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Format date
  const formatMatchDateTime = (dateString, timeString) => {
    if (!dateString) return { date: "Undecided", time: "Undecided", day: "Undecided" };

    try {
      // Handle UK date format (DD/MM/YYYY)
      const [day, month, year] = dateString.split('/');
      const date = new Date(year, month - 1, day);

      let time = "12:00";
      if (timeString && timeString.includes(':')) {
        time = timeString;
      }

      return {
        date: date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
        time: time,
        day: date.toLocaleDateString("en-US", { weekday: "short" }),
        fullDate: `${date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric"
        })} at ${time}`
      };
    } catch (error) {
      return {
        date: dateString,
        time: timeString || "Undecided",
        day: "Undecided",
        fullDate: dateString
      };
    }
  };

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) {
      return "/default-player.png";
    }
    if (imagePath.startsWith("http")) {
      return imagePath;
    }
    return imagePath;
  };

  const getMatchStatusClass = (status) => {
    switch (status) {
      case 'live':
        return styles.liveStatus;
      case 'completed':
        return styles.completedStatus;
      default:
        return styles.upcomingStatus;
    }
  };

  // Render detailed batting card
  const renderBattingCard = (battingData, teamName) => {
    if (!battingData || battingData.length === 0) {
      return (
        <div className={styles.battingCard}>
          <h4>{teamName} - Batting</h4>
          <p>No detailed batting data available</p>
        </div>
      );
    }

    return (
      <div className={styles.battingCard}>
        <h4>{teamName} - Batting</h4>
        <table className={styles.scoreTable}>
          <thead className={styles.thead}>
            <tr>
              <th>Batsman</th>
              <th>Runs</th>
              <th>Balls</th>
              <th>4s</th>
              <th>6s</th>
              <th>SR</th>
              <th>How Out</th>
            </tr>
          </thead>
          <tbody>
            {battingData.map((batsman, index) => (
              <tr key={index}>
                <td>
                  {batsman.batsman_name}
                  {batsman.position <= 3 && " *"}
                </td>
                <td>{batsman.runs || 0}</td>
                <td>{batsman.balls || 0}</td>
                <td>{batsman.fours || 0}</td>
                <td>{batsman.sixes || 0}</td>
                <td>{batsman.balls > 0 ? ((batsman.runs / batsman.balls * 100).toFixed(2)) : "0.00"}</td>
                <td>{batsman.how_out || "not out"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render detailed bowling card
  const renderBowlingCard = (bowlingData, teamName) => {
    if (!bowlingData || bowlingData.length === 0) {
      return (
        <div className={styles.bowlingCard}>
          <h4>{teamName} - Bowling</h4>
          <p>No detailed bowling data available</p>
        </div>
      );
    }

    return (
      <div className={styles.bowlingCard}>
        <h4>{teamName} - Bowling</h4>
        <table className={styles.scoreTable}>
          <thead className={styles.thead} >
            <tr>
              <th>Bowler</th>
              <th>O</th>
              <th>M</th>
              <th>R</th>
              <th>W</th>
              <th>Econ</th>
            </tr>
          </thead>
          <tbody>
            {bowlingData.map((bowler, index) => (
              <tr key={index}>
                <td>{bowler.bowler_name}</td>
                <td>{bowler.overs || 0}</td>
                <td>{bowler.maidens || 0}</td>
                <td>{bowler.runs || 0}</td>
                <td>{bowler.wickets || 0}</td>
                <td>{bowler.overs > 0 ? ((bowler.runs / bowler.overs).toFixed(2)) : "0.00"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPointsTable = (pointsData) => {
    if (!pointsData || pointsData.length === 0) return null;

    return (
      <div className={styles.pointsCard}>
        <h4>Points Awarded</h4>
        <table className={styles.scoreTable}>
          <thead className={styles.thead}>
            <tr>
              <th>Team</th>
              <th>Game Points</th>
              <th>Batting Points</th>
              <th>Bowling Points</th>
              <th>Bonus Points</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {pointsData.map((point, index) => {
              // Determine which team this point belongs to
              const isHomeTeam = point.team_id.toString() === match?.home_team_id?.toString();
              const teamData = isHomeTeam ? match?.team1_data : match?.team2_data;

              return (
                <tr key={index}>
                  <td>
                    {teamData ?
                      `${teamData.club_name} (${teamData.name})` :
                      `Team ${point.team_id}`
                    }
                  </td>
                  <td>{point.game_points || 0}</td>
                  <td>{point.bonus_points_batting || "0.0"}</td>
                  <td>{point.bonus_points_bowling || "0.0"}</td>
                  <td>{point.bonus_points_together || "0.0"}</td>
                  <td><strong>{
                    (parseFloat(point.game_points || 0) +
                      parseFloat(point.bonus_points_batting || 0) +
                      parseFloat(point.bonus_points_bowling || 0) +
                      parseFloat(point.bonus_points_together || 0)).toFixed(1)
                  }</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render simplified score if no detailed data
  const renderSimpleScore = () => {
    if (!match) return null;

    return (
      <div className={styles.simpleScoreCard}>
        <h3>Match Summary</h3>
        <div className={styles.simpleScore}>
          <div className={styles.simpleTeam}>
            <strong>{match.team1_data?.name}</strong>
            <div>{match.score_team1}</div>
          </div>
          <div className={styles.simpleVs}>vs</div>
          <div className={styles.simpleTeam}>
            <strong>{match.team2_data?.name}</strong>
            <div>{match.score_team2}</div>
          </div>
        </div>
        {match.result_description && (
          <div className={styles.simpleResult}>
            <strong>Result:</strong> {match.result_description}
          </div>
        )}
        {match.match_notes && (
          <div className={styles.simpleNotes}>
            <strong>Notes:</strong> {match.match_notes}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.matchDetailsPage}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>


        {/* API Component (invisible, only handles API calls) */}
        <MatchDetailAPI
          matchId={id}
          onDataLoaded={handleMatchDetailData}
          onError={handleAPIError}
          onLoading={handleAPILoading}
        />
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className={styles.matchDetailsPage}>
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <h2>Error Loading Match</h2>
            <p>{error || "The match you're looking for doesn't exist or has been removed."}</p>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              <FaArrowLeft className={styles.icon} /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formattedDateTime = formatMatchDateTime(match.match_date, match.match_time);

  return (
    <div className={styles.matchDetailsPage}>
      {/* API Component (invisible, only handles API calls) */}
      <MatchDetailAPI
        matchId={id}
        onDataLoaded={handleMatchDetailData}
        onError={handleAPIError}
        onLoading={handleAPILoading}
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.container}>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              ← Back
            </button>

            <div className={styles.heroText}>
              <h2 className={styles.heroTitle}>
                <span><p style={{ margin: "0" }}>{match.team1_data.club_name} ({match.team1_data?.name})</p> <span style={{ color: "red", textAlign: "center" }}>vs</span> <p style={{ margin: "0" }}>{match.team2_data.club_name} ({match.team2_data?.name})</p></span>
              </h2>

              {/* Add Result here */}
              {match.result_description && (
                <div className={styles.matchResult}>
                  <h3 className={styles.resultText}>
                    <FaTrophy className={styles.icon} /> {match.result_description}
                  </h3>
                </div>
              )}
            </div>
            <div className={styles.matchInfo}>
              <div className={styles.infoItem}>
                <FaTrophy className={styles.icon} /> {match.competition_name} • {match.match_type}
              </div>
              <div className={styles.infoItem}><FaMapMarkerAlt className={styles.icon} /> {match.venue}</div>
              <div className={styles.infoItem}>
                <FaCalendarAlt className={styles.icon} /> {formattedDateTime.fullDate}
              </div>
              {match.toss && (
                <div className={styles.infoItem}>
                  <FaCoins className={styles.icon} /> {match.toss}
                </div>
              )}
              {match.umpires && match.umpires.length > 0 && (
                <div className={styles.infoItem}>
                  <FaUserTie className={styles.icon} /> Umpires: {match.umpires.join(", ")}
                </div>
              )}
              {match.scorers && match.scorers.length > 0 && (
                <div className={styles.infoItem}>
                  <FaClipboardList className={styles.icon} /> Scorers: {match.scorers.join(", ")}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Teams Section */}
      <section className={styles.teamsSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Squad</h2>
          <div className={styles.teamsGrid}>
            {/* Team 1 */}
            <div className={styles.teamContainer}>
              <div className={styles.teamHeader}>
                <div className={styles.teamInfo}>
                  <h2 className={styles.teamName}>{match.team1_data?.name}</h2>
                  <div className={styles.teamShortName}>{match.team1_data?.short_name}</div>
                  <div className={styles.teamClub}>{match.team1_data?.club_name}</div>
                </div>
              </div>

              <div className={styles.playersSection}>
                <h4 style={{ margin: "4px 0" }}>
                  Playing XI ({team1Players.length})
                </h4>

                {playersLoading ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading Players...</p>
                  </div>
                ) : team1Players.length > 0 ? (
                  <div className={styles.playersGrid}>
                    {[...team1Players]
                      .sort((a, b) => a.position - b.position)
                      .map((player) => (
                        <div key={player.id} className={styles.playerCard}>
                          <div className={styles.playerImageContainer}>
                            <div className={`${styles.positionBadge} ${player.position <= 3 ? styles.topOrderBadge :
                              player.position <= 7 ? styles.middleOrderBadge :
                                styles.lowerOrderBadge
                              }`}>
                              #{player.position}
                            </div>

                          </div>

                          <div className={styles.playerInfo}>
                            <div className={styles.playerName}>
                              {player.name}
                              {player.is_captain && (
                                <span className={styles.captainText}>(C)</span>
                              )}
                              {player.is_wicket_keeper && (
                                <span className={styles.wkText}>(WK)</span>
                              )}
                            </div>

                            {/* Player Role - SIMPLIFIED */}
                            <p className={styles.playerRole}>
                              {getRoleDisplayName(player.role)}
                            </p>

                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className={styles.noPlayers}>
                    <p>No players found for this team</p>
                  </div>
                )}
              </div>
            </div>

            {/* Team 2 */}
            <div className={styles.teamContainer}>
              <div className={styles.teamHeader}>
                <div className={styles.teamInfo}>
                  <h2 className={styles.teamName}>{match.team2_data?.name}</h2>
                  <div className={styles.teamShortName}>{match.team2_data?.short_name}</div>
                  <div className={styles.teamClub}>{match.team2_data?.club_name}</div>
                </div>
              </div>

              <div className={styles.playersSection}>
                <h4 style={{ margin: "4px 0px" }}>
                  Playing XI ({team2Players.length})
                </h4>

                {playersLoading ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Loading Players...</p>
                  </div>
                ) : team2Players.length > 0 ? (
                  <div className={styles.playersGrid}>
                    {[...team2Players]
                      .sort((a, b) => a.position - b.position)
                      .map((player) => (
                        <div key={player.id} className={styles.playerCard}>
                          <div className={styles.playerImageContainer}>
                            <div className={`${styles.positionBadge} ${player.position <= 3 ? styles.topOrderBadge :
                              player.position <= 7 ? styles.middleOrderBadge :
                                styles.lowerOrderBadge
                              }`}>
                              #{player.position}
                            </div>

                          </div>

                          <div className={styles.playerInfo}>
                            <div className={styles.playerName}>
                              {player.name}
                              {player.is_captain && (
                                <span className={styles.captainText}>(C)</span>
                              )}
                              {player.is_wicket_keeper && (
                                <span className={styles.wkText}>(WK)</span>
                              )}
                            </div>

                            {/* Player Role - SIMPLIFIED */}
                            <p className={styles.playerRole}>
                              {getRoleDisplayName(player.role)}
                            </p>

                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className={styles.noPlayers}>
                    <p>No players found for this team</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scorecard Section - Show detailed or simple */}
      <section className={styles.scorecardSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Scorecard</h2>

          {scorecardData ? (
            <>
              {/* Innings 1 */}
              {scorecardData.innings1 && (
                <div className={styles.inningsCard}>
                  <h3 className={styles.inningsTitle}>
                    {scorecardData.innings1.team_name} - {scorecardData.innings1.runs}/{scorecardData.innings1.wickets}
                    ({scorecardData.innings1.overs} overs)
                  </h3>
                  <div className={styles.inningsExtras}>
                    Extras: {scorecardData.innings1.total_extras}
                    (b {scorecardData.innings1.extra_byes},
                    lb {scorecardData.innings1.extra_leg_byes},
                    w {scorecardData.innings1.extra_wides},
                    nb {scorecardData.innings1.extra_no_balls})
                  </div>

                  {renderBattingCard(scorecardData.innings1.batting, scorecardData.innings1.team_name)}
                  {renderBowlingCard(scorecardData.innings1.bowling,
                    scorecardData.innings1.team_id === match.home_team_id ? match.team2_data?.name : match.team1_data?.name)}
                </div>
              )}

              {/* Innings 2 */}
              {scorecardData.innings2 && (
                <div className={styles.inningsCard}>
                  <h3 className={styles.inningsTitle}>
                    {scorecardData.innings2.team_name} - {scorecardData.innings2.runs}/{scorecardData.innings2.wickets}
                    ({scorecardData.innings2.overs} overs)
                  </h3>
                  <div className={styles.inningsExtras}>
                    Extras: {scorecardData.innings2.total_extras}
                    (b {scorecardData.innings2.extra_byes},
                    lb {scorecardData.innings2.extra_leg_byes},
                    w {scorecardData.innings2.extra_wides},
                    nb {scorecardData.innings2.extra_no_balls})
                  </div>

                  {renderBattingCard(scorecardData.innings2.batting, scorecardData.innings2.team_name)}
                  {renderBowlingCard(scorecardData.innings2.bowling,
                    scorecardData.innings2.team_id === match.home_team_id ? match.team2_data?.name : match.team1_data?.name)}
                </div>
              )}
            </>
          ) : (
            renderSimpleScore()
          )}

          {/* Points Table */}
          {match.points && renderPointsTable(match.points)}
        </div>
      </section>

      {/* Match Notes Section */}
      {match.match_notes && (
        <section className={styles.notesSection}>
          <div className={styles.container}>
            <h2 className={styles.summaryTitle}>Match Notes</h2>
            <div
              className={styles.notesContent}
              dangerouslySetInnerHTML={{ __html: match.match_notes.replace(/\r/g, '<br/>') }}
            />
          </div>
        </section>
      )}

      {/* Live Match Actions */}
      {match.status === 'live' && (
        <section className={styles.liveActions}>
          <div className={styles.container}>
            <div className={styles.actionButtons}>
              <button className={styles.watchLiveButton}>
                <FaPlay className={styles.icon} />
                Watch Live Stream
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MatchDetail;